class LiquidationPagination extends HTMLElement {
  constructor() {
    super();
    this.handleLoadMoreClick = this.loadNextPage.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.paginationType = this.dataset.type;
    this.productList = document.querySelector(`ul[data-id="${this.sectionId}"]`);
    this.loadMoreButton = this.querySelector('.btn--load-more');
    this.totalProducts = Number.parseInt(
      this.productList?.dataset.productsCount || '0',
      10
    );
    this.currentPage =
      Number.parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10) + 1;

    if (!this.productList || !this.loadMoreButton) return;

    if (this.paginationType === 'infinite-scroll') {
      this.setupInfiniteScroll();
      return;
    }

    this.loadMoreButton.addEventListener('click', this.handleLoadMoreClick);
  }

  setupInfiniteScroll() {
    this.scrollObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) this.loadNextPage();
      },
      { rootMargin: '300px 0px', threshold: 0.01 }
    );

    this.scrollObserver.observe(this);
  }

  buildNextPageUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('page', this.currentPage);
    return url.toString();
  }

  async loadNextPage() {
    if (this.hasAttribute('loading')) return;

    this.setAttribute('loading', '');
    this.setLoadingState(true);

    try {
      const response = await fetch(this.buildNextPageUrl(), {
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) {
        throw new Error(`Pagination request failed with status ${response.status}`);
      }

      const html = await response.text();
      const { addedProducts, hasNextPage } = this.appendFetchedProducts(html);

      if (!hasNextPage) {
        this.finalizePagination();
        return;
      }

      this.currentPage += 1;

    } catch (error) {
      console.error('Unable to load more liquidation products:', error);
    } finally {
      this.setLoadingState(false);
      this.removeAttribute('loading');
    }
  }

  appendFetchedProducts(html) {
    const nextDocument = new DOMParser().parseFromString(html, 'text/html');
    const nextProductGrid = nextDocument.getElementById('product-grid');

    if (!nextProductGrid) return { addedProducts: 0, hasNextPage: false };

    const nextProducts = nextProductGrid.querySelectorAll(
      '.liquidation-product-list__item'
    );

    nextProducts.forEach((product) => {
      this.productList.appendChild(product);
    });

    return {
      addedProducts: nextProducts.length,
      hasNextPage: Boolean(nextDocument.querySelector('custom-pagination')),
    };
  }

  getLoadedProductCount() {
    return this.productList.querySelectorAll(
      '.liquidation-product-list__item'
    ).length;
  }

  setLoadingState(isLoading) {
    if (!this.loadMoreButton) return;

    this.loadMoreButton.classList.toggle('loading', isLoading);
    this.loadMoreButton.disabled = isLoading;
    this.loadMoreButton.setAttribute('aria-busy', String(isLoading));

    const spinner = this.loadMoreButton.querySelector('.loading__spinner');
    spinner?.classList.toggle('hidden', !isLoading);
  }

  finalizePagination() {
    this.scrollObserver?.disconnect();
    this.loadMoreButton?.classList.add('visually-hidden');
  }

  disconnectedCallback() {
    this.scrollObserver?.disconnect();
    this.loadMoreButton?.removeEventListener('click', this.handleLoadMoreClick);
  }
}

if (!customElements.get('custom-pagination')) {
  customElements.define('custom-pagination', LiquidationPagination);
}
